import './styles/ShibUserDetail.scss';

import { useQuery } from '@apollo/client';
import { Avatar, Chip, Grid, Paper } from '@mui/material';
import { ShibUser } from '@thro-types/users';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDarkReasonable } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { stringToColour } from 'utils';

import QUERY from 'gql/queries/getUser.gql';

import DetailPage from 'app/components/DetailPage';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { MessagingSettings } from './components/MessagingSettings';

export function ShibUserDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  let user: ShibUser;

  usePageHeading({
    heading: t(translations.layout.menu.shibUserManagement()),
    headingProps: {
      back: '/shib/users'
    }
  });

  const getUserQuery = useQuery<any, any>(QUERY, {
    variables: { id: parseInt(id!, 10) },
    context: { clientName: 'federation' }
  });

  const SideInfo = () => {
    const [avatarBackground, color] = stringToColour(user.email);
    return (
      <Grid container className="shibUserDetail__side-info">
        <Grid container justifyContent="center">
          <Avatar
            className="shibUserDetail__side-info__avatar"
            component={Paper}
            style={{ backgroundColor: avatarBackground, color }}
          >
            {user.firstName.charAt(0).toUpperCase()}
            {user.lastName.charAt(0).toUpperCase()}
          </Avatar>
        </Grid>
        <Grid container justifyContent="center" className="shibUserDetail__side-info__name">
          {user.firstName} {user.lastName}
        </Grid>
        <Grid container justifyContent="center" className="shibUserDetail__side-info__role">
          {user.role}
        </Grid>
        <Grid container>
          <Grid container className="shibUserDetail__side-info__contact">
            <Grid container>
              <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                {t(translations.pages.shibUserList.uid())}:
              </Grid>
              <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                {user.uid}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                {t(translations.common.shorthand())}:
              </Grid>
              <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                {user.shorthand}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                {t(translations.common.email_short())}:
              </Grid>
              <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                {user.email}
              </Grid>
            </Grid>
            <Grid container className="shibUserDetail__side-info__study">
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.course())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {user.courseOfStudy ? user.courseOfStudy : '---'}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.semester())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {user.semester ? user.semester : '---'}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.degree())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {user.degree ? user.degree : '---'}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.locationOfStudy())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {user.locationOfStudy ? user.locationOfStudy : '---'}
                </Grid>
              </Grid>
            </Grid>
            <Grid container className="shibUserDetail__side-info__dates">
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.createdAt())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {new Date(user.createdAt?.toString() || '').toLocaleDateString() +
                    ' ' +
                    new Date(user.createdAt?.toString() || '').toLocaleTimeString()}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} md={4} className="shibUserDetail__side-info__contact__label">
                  {t(translations.common.updatedAt())}:
                </Grid>
                <Grid item xs={12} md={8} className="shibUserDetail__side-info__contact__content">
                  {new Date(user.modifiedAt?.toString() || '').toLocaleDateString() +
                    ' ' +
                    new Date(user.modifiedAt?.toString() || '').toLocaleTimeString()}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <GQLGuard
      query={getUserQuery}
      onError={error => {
        if (error.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.shibUserList.alerts.loadUserNotFoundError())}`
            )
          );
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.shibUserList.alerts.loadUserError())}: ${error.message}`
            )
          );
        }
        navigate('/shib/users', { replace: true });
        return null;
      }}
    >
      {data => {
        user = data.user;
        return (
          <DetailPage className="shibUserDetail" sideInfo={<SideInfo />}>
            <Grid container className="shibUserDetail__info">
              <Grid container>
                <Grid container className="shibUserDetail__info__headline">
                  {t(translations.pages.shibUserList.communities())}:
                </Grid>
                <Grid container className="shibUserDetail__info__communities">
                  {user.communities.map(community => (
                    <Chip label={community} />
                  ))}
                </Grid>
              </Grid>
              <Grid container>
                <Grid container className="shibUserDetail__info__headline">
                  {t(translations.pages.shibUserList.roles())}:
                </Grid>
                <Grid container className="shibUserDetail__info__roles">
                  {user.roles?.map(role => (
                    <Chip label={role} />
                  ))}
                </Grid>
              </Grid>
              <Grid container>
                <Grid container className="shibUserDetail__info__headline">
                  {t(translations.pages.shibUserList.entitlements())}:
                </Grid>
                <Grid container className="shibUserDetail__info__entitlements">
                  {user.entitlements.map(entitlement => (
                    <Chip label={entitlement} />
                  ))}
                </Grid>
              </Grid>
              <Grid container>
                <Grid container className="shibUserDetail__info__headline">
                  {t(translations.pages.shibUserList.meta())}:
                </Grid>
                <Grid container>
                  <SyntaxHighlighter
                    language="json"
                    style={atomOneDarkReasonable}
                    customStyle={{ width: '100%' }}
                  >
                    {JSON.stringify(JSON.parse(user.meta || ''), null, 2)}
                  </SyntaxHighlighter>
                </Grid>
              </Grid>
            </Grid>
            <Grid container style={{ marginTop: '2em' }}>
              <MessagingSettings user={user} />
            </Grid>
          </DetailPage>
        );
      }}
    </GQLGuard>
  );
}
